import React, {useContext, useState} from "react";
import TemplatePreviewContentList from "./templatePreviewContentList";
import {ActionType, PresentationStyle, TimeFormat} from "../../util/enum";
import {CampaignProgress} from "./campaignProgressBar";
import {differ, formatDate, formatDateTimeToAMPM} from "../../util/util";
import moment from "moment";
import {
    CampaignSetting,
    convertDateTimeToUTC,
    dataFoAppointmentReminder,
    dataForPostAppointment,
    dataFoRecurrence,
    dataForMessagePerDay,
    FireType, offsetMinutesWithDateTime,
    validateDateTime,
} from "./dataForDesignCampaign";
import {ProviderContext} from "../providerContext";
import {
    Employee,
    MessageTemplateDetails,
    saveCampaign,
    updateAutomatedCampaign,
    updateRegularCampaign,
} from "./campaignService";
import Spinner from "../../components/spinner/spinner";
import {TemplateContent} from "./templateEditContentList";
import Button from "../../components/button/button";
import {ButtonStatus} from "../../components/button/enum";
import {PromptContext} from "../promptContext";
import {AutomatedCampaignType, CampaignBigType, CampaignType} from "./dataForActiveCampaign";
import {DropdownListFormNotSelectedStringId} from "../../components/forms/dropdownListForm";
import {ReplaceableInfo, replaceWithReplaceableInfo} from "./templateEditor";
import CustomModal from "../../components/modal/customModal";
import PatientPreviewList from "../patients/patientPreviewList";
import {GraylogContext} from "../graylogContext";
import {GrayLog} from "../../grayLog/grayLog";
import {LogEventName} from "../../grayLog/logKey";
import {$editIcon} from "../../assets/icons";
import {MaxMatchingPatientCount} from "../patients/newGroup";

interface IProps {
    actionType: ActionType,
    setting: CampaignSetting,
    dataForTemplateLanguage: Array<TemplateContent>,
    replaceableInfo?: ReplaceableInfo,
    presentationStyle?: PresentationStyle,
    onBack?: (progress?: CampaignProgress) => void,
    onFinish?: () => void
}

function PreviewCampaign(props: IProps) {
    const {onBack, onFinish, dataForTemplateLanguage, replaceableInfo, presentationStyle = PresentationStyle.push, setting, actionType} = props
    const provider = useContext(ProviderContext)
    const {showSuccess, showError, showInfo} = useContext(PromptContext)
    const logEmployee = useContext(GraylogContext)
    const [showSpinner, setShowSpinner] = useState<boolean>(false)
    const [showPatientPreviewModal, setShowPatientPreviewModal] = useState<boolean>(false)

    const $mainItem = (title: string, value: string | any, showIcon?: boolean, progress?: CampaignProgress) => {
        return (
            <div className={"w-full flex flex-col lg:flex-row items-start lg:items-center justify-between"}>
                <p className={"font-semibold text-base text-gray-2 flex-none"}>{title}</p>
                <div className={"w-full flex flex-fow items-center justify-between lg:justify-end"}>
                    <p className={"text-sm lg:text-base text-input text-right"}>{value}</p>
                    <button onClick={() => {
                        onBack && onBack(progress)
                    }} className={`hover:bg-theme-blue-10 ml-2 flex-none ${showIcon ? "" : "hidden"}`}>
                        {$editIcon}
                    </button>
                </div>
            </div>
        )
    }

    const $subItem = (title: string, value: string | any) => {
        return (
            <div className={"flex flex-col lg:flex-row items-start lg:items-center lg:justify-between pl-6 my-4"}>
                <p className={"font-semibold text-base text-gray-2"}>{title}</p>
                <p className={"text-base text-input"}>{value}</p>
            </div>
        )
    }
    const {campaignId, relationId, campaignName, startDate, endDate, sendDateTime, campaignType, messagePerDay, templateId, fireType,
        campaignBigType, automatedCampaignType, groupName, olid, groupId, matchingCount, excludePatients, beforeDay,
    } = setting

    let campaignBigTypeName = "Regular campaign"
    if (setting.campaignBigType === CampaignBigType.automatedCampaign) {
        campaignBigTypeName = "Automated Campaign"
    }

    let _newStartDate = startDate
    let _newEndDate = endDate
    if (setting.campaignBigType === CampaignBigType.automatedCampaign && actionType !== ActionType.view) {
        if (!validateDateTime(startDate, sendDateTime)) {
            const offsetMinutes = offsetMinutesWithDateTime(startDate, sendDateTime)
            const offsetDays = Math.ceil(Math.abs(offsetMinutes) / (24 * 60))
            _newStartDate = moment(startDate).add(offsetDays, "day")
                .toDate()
            _newEndDate = _newStartDate
        }
    }

    const type = `${campaignBigTypeName} - SMS`
    let startDateTimeString = formatDate(moment(_newStartDate).format(TimeFormat.YYYYMMDDHHmmss)) + ", " + formatDateTimeToAMPM(sendDateTime)
    const endDateString = formatDate(moment(_newEndDate).format(TimeFormat.YYYYMMDDHHmmss))

    const campaignTypeItem = dataFoRecurrence.find((_item) => _item.id === campaignType)

    const {officeLocations, employeeFirstName, employeeLastName} = provider
    const officeLocation = officeLocations.find((_office) => _office.olid === olid)
    const clinicName = officeLocation?.clinicName || provider.clinicID

    const doctorName = `Dr. ${employeeFirstName} ${employeeLastName}`
    const patientList = `${clinicName} - ${doctorName}`

    const previewPatientList = () => {
        if (!matchingCount) {
            return
        }
        if (matchingCount <= MaxMatchingPatientCount) {
            setShowPatientPreviewModal(true)
        } else {
            showInfo("Cannot preview more than 1,000 patients.")
        }
    }

    const $matchingCount = campaignBigType === CampaignBigType.regularCampaign ? (
        <span className={"inline-block font-bold text-primary cursor-pointer"} onClick={previewPatientList}>
            {matchingCount}
            <span className={"text-input font-normal ml-1"}>{"Patients"}</span>
        </span>
    ) : null

    const messageMaxPerDayItem = dataForMessagePerDay.find((_item) => {
        return _item.id === messagePerDay
    })
    const messageMaxPerDay = messageMaxPerDayItem ? messageMaxPerDayItem.name.toString() : ""

    const onClickBack = () => {
        onBack && onBack()
    }

    const getMessageDetails = (): Array<MessageTemplateDetails> => {
        return dataForTemplateLanguage.map((_item) => {
            const replaceInfo: ReplaceableInfo = {
                providerName: replaceableInfo?.providerName,
                practiceName: replaceableInfo?.practiceName,
                officePhone: replaceableInfo?.officePhone,
            }
            return {
                language: _item.language,
                libMessage: _item.libMessage,
                compliedMessage: replaceWithReplaceableInfo(_item.compliedMessage, replaceInfo),
            }
        })
    }

    const onSend = () => {
        const startSendDateTimeUTC = fireType === FireType.sendNow ? (new Date()).toISOString() : convertDateTimeToUTC(_newStartDate, sendDateTime)
        const endSendDateTimeUTC = fireType === FireType.sendNow ? (new Date().toISOString()) : convertDateTimeToUTC(_newEndDate, sendDateTime)

        const employee: Employee = {
            clinicID: provider.clinicID,
            employeeID: provider.employeeID,
            mEmployeeID: provider.mEmployeeID,
            employeeFirstName: provider.employeeFirstName,
            employeeMidName: provider.employeeMidName,
            employeeLastName: provider.employeeLastName,
        }

        let optionalFilter: any = {}
        if (setting.campaignBigType === CampaignBigType.automatedCampaign) {
            if (setting.filter.planType && setting.filter.planType !== DropdownListFormNotSelectedStringId) {
                optionalFilter.planType = [setting.filter.planType]
            }
            if (setting.filter.fromAge.length) {
                optionalFilter.fromAge = setting.filter.fromAge
            }
            if (setting.filter.toAge.length) {
                optionalFilter.toAge = setting.filter.toAge
            }
        }

        setShowSpinner(true)
        saveCampaign(
            campaignName,
            startSendDateTimeUTC,
            endSendDateTimeUTC,
            campaignBigType,
            campaignType,
            automatedCampaignType,
            1,
            messagePerDay,
            templateId.length ? templateId : undefined,
            [groupId],
            employee,
            templateId.length ? templateId : undefined,
            getMessageDetails(),
            excludePatients,
            beforeDay,
            optionalFilter,
            () => {
                setShowSpinner(false)
                showSuccess("Your campaign has been created.", onClickSaveSuccessClose)
                GrayLog.event(LogEventName.newCampaign, {isSuccessful: true, campaignType: setting.campaignBigType, automatedCampaignType: setting.automatedCampaignType, ...logEmployee})
            },
            (error) => {
                setShowSpinner(false)
                showError(error)
                const errMsg = `${error.code} - ${error.msg}`
                GrayLog.event(LogEventName.newCampaign, {isSuccessful: false, errMsg: errMsg, campaignType: setting.campaignBigType, automatedCampaignType: setting.automatedCampaignType, ...logEmployee})
            }
        )
    }

    const onUpdateRegularCampaign = () => {
        setShowSpinner(true)
        updateRegularCampaign(
            campaignId,
            campaignName,
            messagePerDay,
            beforeDay,
            getMessageDetails(),
            convertDateTimeToUTC(_newStartDate, sendDateTime),
            (isSuccess) => {
                setShowSpinner(false)
                if (isSuccess) {
                    showSuccess("Your campaign has been updated.", onClickSaveSuccessClose)
                } else {
                    showError("Update failed!")
                }
                GrayLog.event(LogEventName.updateCampaign, {isSuccessful: true, campaignType: setting.campaignBigType, automatedCampaignType: setting.automatedCampaignType, ...logEmployee})
            },
            (error) => {
                setShowSpinner(false)
                showError(error)
                const errMsg = `${error.code} - ${error.msg}`
                GrayLog.event(LogEventName.updateCampaign, {isSuccessful: false, errMsg: errMsg, campaignType: setting.campaignBigType, automatedCampaignType: setting.automatedCampaignType, ...logEmployee})
            })
    }

    const onUpdateAutomatedCampaign = () => {
        let optionalFilter: any = {}
        if (setting.campaignBigType === CampaignBigType.automatedCampaign) {
            if (setting.filter.planType && setting.filter.planType !== DropdownListFormNotSelectedStringId) {
                optionalFilter.planType = [setting.filter.planType]
            }
            if (setting.filter.fromAge.length) {
                optionalFilter.fromAge = setting.filter.fromAge
            }
            if (setting.filter.toAge.length) {
                optionalFilter.toAge = setting.filter.toAge
            }
        }

        setShowSpinner(true)
        updateAutomatedCampaign(
            campaignId,
            campaignName,
            messagePerDay,
            beforeDay,
            getMessageDetails(),
            convertDateTimeToUTC(_newStartDate, sendDateTime),
            relationId,
            optionalFilter,
            (isSuccess) => {
                setShowSpinner(false)
                if (isSuccess) {
                    showSuccess("Your campaign has been updated.", onClickSaveSuccessClose)
                } else {
                    showError("Update failed!")
                }
                GrayLog.event(LogEventName.updateCampaign, {isSuccessful: true, campaignType: setting.campaignBigType, automatedCampaignType: setting.automatedCampaignType, ...logEmployee})
            },
            (error) => {
                setShowSpinner(false)
                showError(error)
                const errMsg = `${error.code} - ${error.msg}`
                GrayLog.event(LogEventName.updateCampaign, {isSuccessful: false, errMsg: errMsg, campaignType: setting.campaignBigType, automatedCampaignType: setting.automatedCampaignType, ...logEmployee})
            })
    }

    const onUpdateCampaign = () => {
        if (setting.campaignBigType === CampaignBigType.automatedCampaign) {
            onUpdateAutomatedCampaign()
        } else if (setting.campaignBigType === CampaignBigType.regularCampaign) {
            onUpdateRegularCampaign()
        }
    }

    const onClickSaveSuccessClose = () => {
        differ(() => {
            onFinish && onFinish()
        })
    }

    const $spinner = <Spinner isOpen={showSpinner} />

    const isScheduleForLater = setting.fireType === FireType.scheduleLater
    const showEditIcon = actionType !== ActionType.view

    const $subItemForPatientGroup = $subItem("Patient Group", groupName)
    const $subItemForPatientList = $subItem("Patient List", patientList)

    const $closeBtn = (
        <Button status={ButtonStatus.float} onClick={() => {
            onBack && onBack()
        }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.5 4.5L14.0806 14.0806" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4.5 14.0811L14.0806 4.50041" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </Button>
    )

    const $tableTitle = (
        <div className={"flex flex-col"}>
            <div className={"flex flex-row justify-between items-start"}>
                <div className={"flex flex-col"}>
                    <p className={"font-bold text-2xl"}>Summary</p>
                    <p className={"text-gray-2 mt-2 font-base text-base"}>Review your campaign details.</p>
                </div>
                {presentationStyle === PresentationStyle.modal ? $closeBtn : null}
            </div>
            <div className={"w-full h-px bg-gray-300 mt-4"}/>
        </div>
    )
    const $tableForRegularCampaign = (
        <div className={"flex flex-col space-y-4"}>
            {$tableTitle}
            {$mainItem("Campaign Name", campaignName)}
            {$mainItem("Type", type)}
            {isScheduleForLater ? null : $mainItem("Start Date", formatDate(moment().format(TimeFormat.YYYYMMDDHHmmss)), showEditIcon, CampaignProgress.designCampaign)}
            {isScheduleForLater ? $mainItem("Start Date & Time (EST)", startDateTimeString, showEditIcon, CampaignProgress.designCampaign) : null}
            {isScheduleForLater ? $mainItem("End Date", endDateString, showEditIcon, CampaignProgress.designCampaign) : null}
            {campaignType !== CampaignType.sendOnce ? $mainItem("Recurrence", campaignTypeItem?.name, showEditIcon, CampaignProgress.designCampaign) : null}
            {$mainItem("Recipients", $matchingCount, showEditIcon, CampaignProgress.group)}
            {$subItemForPatientGroup}
            {$subItemForPatientList}
            {$mainItem("Message Per Day", messageMaxPerDay, showEditIcon, CampaignProgress.designCampaign)}
        </div>
    )

    let descForFilter = ""
    let autoStartDate = ""
    if (automatedCampaignType === AutomatedCampaignType.appointment) {
        let beforeDayItem = dataFoAppointmentReminder.find((item) => item.id === setting.beforeDay)
        if (beforeDayItem) {
            autoStartDate = `${beforeDayItem.name}, starting from ${startDateTimeString}`
        } else {
            autoStartDate = startDateTimeString
        }
    } else if (automatedCampaignType === AutomatedCampaignType.postAppointment) {
        let afterDayItem = dataForPostAppointment.find((item) => item.id === setting.beforeDay)
        if (afterDayItem) {
            autoStartDate = `${afterDayItem.name}, starting from ${startDateTimeString}`
        } else {
            autoStartDate = startDateTimeString
        }
    } else if (automatedCampaignType === AutomatedCampaignType.birthday) {
        if (setting.filter.fromAge.length && setting.filter.toAge.length) {
            descForFilter += `Age: ${setting.filter.fromAge} - ${setting.filter.toAge}`
        } else if (setting.filter.fromAge.length && !setting.filter.toAge.length) {
            descForFilter += `Age: >= ${setting.filter.fromAge}`
        } else if (!setting.filter.fromAge.length && setting.filter.toAge.length) {
            descForFilter += `Age: <= ${setting.filter.toAge}`
        }
        // if (setting.filter.planType) {
        //     const planItem = dataForPlanType.find((plan) => {
        //         return plan.id !== DropdownListFormNotSelectedStringId && plan.id === setting.filter.planType
        //     })
        //     if (planItem) {
        //         descForFilter += `; Type: ${planItem.name}`
        //     }
        // }

        autoStartDate = `On Patient’s Birthday, ${formatDateTimeToAMPM(sendDateTime)}`
    }
    const $tableForAutomatedCampaign = (
        <div className={"flex flex-col space-y-4"}>
            {$tableTitle}
            {$mainItem("Campaign Name", campaignName)}
            {$mainItem("Type", type)}
            {$mainItem("Start Date & Time (EST)", autoStartDate, showEditIcon, CampaignProgress.designCampaign)}
            {automatedCampaignType === AutomatedCampaignType.birthday ? $mainItem("Recipients", $matchingCount, showEditIcon, CampaignProgress.group) : null}
            {automatedCampaignType === AutomatedCampaignType.birthday ? $subItem("Patient Filter", descForFilter || "N/A") : null}
            {/*{$subItemForPatientList}*/}
            {/*{$mainItem("Message Per Day", messageMaxPerDay, showEditIcon, CampaignProgress.designCampaign)}*/}
        </div>
    )

    const $templateList = (
        <div className={"flex flex-col space-y-4"}>
            {$mainItem("Campaign Content", "", showEditIcon, CampaignProgress.designCampaign)}
            <TemplatePreviewContentList data={dataForTemplateLanguage} replaceableInfo={replaceableInfo}/>
        </div>
    )

    const $saveCampaignButton = (
        <Button onClick={onSend} status={ButtonStatus.primary} >
            {setting.fireType === FireType.sendNow ? "Send" : "Create"}
        </Button>
    )

    const $updateCampaignButton = (
        <Button onClick={onUpdateCampaign} status={ButtonStatus.primary} >
            Update
        </Button>
    )

    const $saveButton = setting.campaignId.length ? $updateCampaignButton : $saveCampaignButton
    const $backButton = (
        <Button onClick={onClickBack} status={ButtonStatus.light} >
            Back
        </Button>
    )
    const $footerButtons = actionType !== ActionType.view ? (
        <div className={"flex flex-row items-center justify-between"}>
            {$backButton}
            {$saveButton}
        </div>
    ) : null

    const $table = setting.campaignBigType === CampaignBigType.regularCampaign ? $tableForRegularCampaign : $tableForAutomatedCampaign
    const $contentView = (
        <div className={"flex flex-col space-y-6"}>
            {$table}
            {$templateList}
            {$footerButtons}
        </div>
    )

    const $patientPreviewModal = (
        <CustomModal isOpen={showPatientPreviewModal}>
            <PatientPreviewList
                groupId={setting.groupId}
                actionType={ActionType.view}
                onClose={() => {
                    setShowPatientPreviewModal(false)
                }}
            />
        </CustomModal>
    )

    return (
        <div className={`w-full p-6 bg-white shadow-lg rounded-lg ${presentationStyle === PresentationStyle.push ? "lg:p-10" : "lg:p-8"}`}>
            {$spinner}
            {$contentView}
            {$patientPreviewModal}
        </div>
    )
}

export default PreviewCampaign
